import React, { useState } from "react";
import axios from "axios";
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import SessionBrowser from './components/SessionBrowser';
import NoticeBanner from './components/NoticeBanner';
import './index.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://vp.matt-tye.com";

function App() {
  const [zpl, setZpl] = useState("^XA^FO50,50^A0N,50,50^FDHello, ZPL!^FS^XZ");
  const [width, setWidth] = useState(101.6);
  const [height, setHeight] = useState(152.4);
  const [dpmm, setDpmm] = useState(8);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleZplChange = (e) => {
    setZpl(e.target.value);
  };

  const handleRender = async () => {
    setLoading(true);
    setError("");
    setPreviewUrl(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/render`,
        { zpl, width, height, dpmm, format: "png" },
        { responseType: "arraybuffer" }
      );
      const blob = new Blob([response.data], { type: "image/png" });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setLoading(false);
  };

  const handleDownload = async (format) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/render`,
        { zpl, width, height, dpmm, format },
        { responseType: "arraybuffer" }
      );
      const blob = new Blob([response.data], { type: format === "pdf" ? "application/pdf" : "image/png" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `label.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Failed to download ${format.toUpperCase()}: ${err.response?.data?.error || err.message}`);
    }
  };

  return (
    <Router>
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#f5f5f7" }}>
        <header style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 32px",
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          position: "sticky",
          top: 0,
          zIndex: 100
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{
              background: "#6366f1",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 15,
              fontWeight: 700,
              color: "#fff",
              letterSpacing: 0.5,
              fontFamily: "monospace"
            }}>
              VZPrint
            </span>
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>by Automtt</span>
          </div>
          <nav style={{ display: "flex", gap: 24 }}>
            <Link to="/" style={{ color: '#fff', textDecoration: 'none', fontWeight: 500, fontSize: 14, opacity: 0.9 }}>Home</Link>
            <Link to="/sessions" style={{ color: '#fff', textDecoration: 'none', fontWeight: 500, fontSize: 14, opacity: 0.9 }}>Sessions</Link>
          </nav>
        </header>

        <NoticeBanner />

        <Routes>
          <Route path="/" element={
            <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: 32 }}>
              <div className="vzprint-main-layout">
                <div className="card editor-card">
                  <h3 className="card-title">ZPL Code</h3>
                  <textarea
                    className="zpl-textarea"
                    value={zpl}
                    onChange={handleZplChange}
                  />
                  <div className="input-row">
                    <div className="input-group">
                      <label>Width (mm)</label>
                      <input
                        type="number"
                        value={width}
                        min={1}
                        max={300}
                        onChange={(e) => setWidth(Number(e.target.value))}
                      />
                    </div>
                    <div className="input-group">
                      <label>Height (mm)</label>
                      <input
                        type="number"
                        value={height}
                        min={1}
                        max={500}
                        onChange={(e) => setHeight(Number(e.target.value))}
                      />
                    </div>
                    <div className="input-group">
                      <label>Dpmm</label>
                      <input
                        type="number"
                        value={dpmm}
                        min={1}
                        max={24}
                        onChange={(e) => setDpmm(Number(e.target.value))}
                      />
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleRender}
                    disabled={loading}
                    style={{ width: "100%", marginBottom: 12 }}
                  >
                    {loading ? "Rendering..." : "Render Label"}
                  </button>
                  {error && <div className="error-message">{error}</div>}
                </div>

                <div className="card preview-card">
                  <h3 className="card-title">Preview</h3>
                  <div className="preview-container">
                    {previewUrl ? (
                      <>
                        <img
                          src={previewUrl}
                          alt="Label Preview"
                          className="preview-image"
                        />
                        <div className="download-buttons">
                          <button className="btn btn-primary" onClick={() => handleDownload("png")}>
                            Download PNG
                          </button>
                          <button className="btn btn-secondary" onClick={() => handleDownload("pdf")}>
                            Download PDF
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="preview-placeholder">
                        Enter ZPL code and click Render to preview your label
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </main>
          } />
          <Route path="/virtual-printer" element={
            <main style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 32, minHeight: 'calc(100vh - 128px)' }}>
              <div className="card" style={{ maxWidth: 500, textAlign: 'center', padding: '3rem 2rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
                <h2 style={{ color: '#1a1a2e', marginBottom: '1rem' }}>Virtual Printer Discontinued</h2>
                <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  The static virtual printer service is now part of our paid licensing model.
                  The free tier ended on 31 July 2026.
                </p>
                <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                  Contact <a href="mailto:sales@matt-tye.com" style={{ color: '#4f46e5', fontWeight: 600 }}>sales@matt-tye.com</a> to discuss licensing options.
                </p>
                <Link to="/" style={{
                  display: 'inline-block',
                  padding: '0.625rem 1.5rem',
                  background: '#4f46e5',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9375rem',
                  fontWeight: 500
                }}>Back to Editor</Link>
              </div>
            </main>
          } />
          <Route path="/sessions" element={
            <main style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: 32 }}>
              <div className="vzprint-main-layout" style={{ maxWidth: 1200 }}>
                <div className="card" style={{ flex: 1, maxWidth: '100%' }}>
                  <SessionBrowser />
                </div>
              </div>
            </main>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
